import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Bot,
  Sparkles,
  Wrench,
  Network,
  ArrowRight,
  Activity,
  Blocks,
  BookOpen,
  MessageSquare,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { useAgentDiscover } from '../hooks/useAgentDiscover';
import { getAgentIcon, getCategoryForDomain } from '../agentVisuals';
import { SkillsLibraryPanel } from '../components/SkillsLibraryPanel';

export default function CreateAgentPage() {
  const navigate = useNavigate();
  const activeTab = 'create';
  const discoverQuery = useAgentDiscover();

  const templates = useMemo(() => {
    const agents = discoverQuery.data ?? [];
    return agents
      .filter((a) => a.agent_type !== 'app' && a.agent_type !== 'external')
      .slice(0, 12)
      .map((a) => {
        const id = a.agent_id || a.id || '';
        const domain = a.domain || 'system';
        const category = getCategoryForDomain(domain);
        const Icon = getAgentIcon(domain, id);
        return {
          id,
          name: a.name || id,
          description: a.description || 'Platform agent workflow',
          icon: Icon,
          color: `bg-${category.color}-100 text-${category.color}-600`,
          domain,
        };
      });
  }, [discoverQuery.data]);

  return (
    <div className="h-full flex flex-col">
      <div className="border-b bg-white px-6 pt-4 pb-0">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-violet-600 flex items-center justify-center">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Agent Manager</h1>
            <p className="text-xs text-gray-500">Build, run, and observe your agentic workflows</p>
          </div>
        </div>

        <nav className="flex gap-1 -mb-px">
          {[
            { id: 'hub', label: 'Agents', icon: Bot, path: '/agents/hub' },
            { id: 'create', label: 'Create', icon: Sparkles, path: '/agents/create' },
            { id: 'observe', label: 'Observe', icon: Activity, path: '/agents/observe' },
            { id: 'ecosystem', label: 'Ecosystem', icon: Network, path: '/agents/ecosystem' },
            { id: 'steps', label: 'Steps', icon: Blocks, path: '/agents/steps' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => navigate(tab.path)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-violet-600 text-violet-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="flex-1 overflow-auto bg-gray-50 p-6">
        <Card className="mb-8 border-violet-200 bg-gradient-to-br from-violet-50 via-white to-indigo-50 overflow-hidden">
          <CardContent className="p-0">
            <div className="grid grid-cols-5">
              <div className="col-span-3 p-8">
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare className="w-5 h-5 text-violet-600" />
                  <Badge className="bg-violet-100 text-violet-700 text-xs">Recommended</Badge>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Agent Custodian</h2>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Describe what you need in natural language and the Custodian will design a complete
                  Volcano workflow for you — choosing the right steps, MCP tools, models, and guardrails.
                </p>
                <div className="flex gap-3">
                  <Button
                    className="bg-violet-600 hover:bg-violet-700"
                    onClick={() => navigate('/agents/create/custodian')}
                  >
                    <Sparkles className="w-4 h-4 mr-1.5" />
                    Start Conversation
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/agents/create/knowledge')}>
                    <BookOpen className="w-4 h-4 mr-1.5" />
                    Rules & Best Practices
                  </Button>
                </div>
              </div>
              <div className="col-span-2 bg-gradient-to-br from-violet-100 to-indigo-100 p-8 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-20 h-20 rounded-2xl bg-white/80 shadow-lg flex items-center justify-center mx-auto mb-3">
                    <Bot className="w-10 h-10 text-violet-600" />
                  </div>
                  <p className="text-sm font-medium text-violet-800">Powered by Claude</p>
                  <p className="text-xs text-violet-600 mt-0.5">Conversational Agent Design</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <SkillsLibraryPanel className="mb-8" />

        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Platform Agent Templates</h3>
            <p className="text-sm text-gray-500">From live agent discovery — click to start with Custodian</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/agents/volcano/new/edit')}>
            <Wrench className="w-4 h-4 mr-1.5" />
            Expert Mode — Blank Workflow
          </Button>
        </div>

        {discoverQuery.isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-8">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading agents from registry…
          </div>
        )}

        {discoverQuery.isError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex gap-2 text-sm text-red-800">
            <AlertCircle className="w-4 h-4 shrink-0" />
            Could not load agents: {(discoverQuery.error as Error)?.message || 'unknown error'}
          </div>
        )}

        {discoverQuery.isSuccess && templates.length === 0 && (
          <p className="text-sm text-gray-500 py-4">No discoverable platform agents returned.</p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {templates.map((tpl) => (
            <Card
              key={tpl.id}
              className="bg-white hover:shadow-md transition-shadow cursor-pointer group"
              onClick={() => navigate(`/agents/create/custodian?template=${encodeURIComponent(tpl.id)}`)}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-violet-100 text-violet-600`}>
                    <tpl.icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-gray-900 truncate">{tpl.name}</h4>
                    <p className="text-xs text-gray-500 mt-0.5">{tpl.domain}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-violet-500 transition-colors mt-1" />
                </div>
                <p className="text-xs text-gray-600 leading-relaxed line-clamp-3">{tpl.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
